"use strict";
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useExperiment = void 0;
var react_1 = require("react");
/**
 * Helper hook for experiments that live in React.
 *
 * Returns undefined until Optimize has chosen a variant ID for the experiment.
 */
function useExperiment(experimentId, options) {
    // using useRefs to ensure we can always access the most recent state in useEffects
    var intervalId = (0, react_1.useRef)();
    var timeout = (0, react_1.useRef)();
    var intervalRunning = (0, react_1.useRef)(false);
    var timeoutRunning = (0, react_1.useRef)(false);
    var _a = __read((0, react_1.useState)(), 2), variantId = _a[0], setVariantId = _a[1];
    // set variantId state once Optimize is ready with its chosen variant id
    (0, react_1.useEffect)(function () {
        // perform a "disable" that conforms to the rules of hooks by allowing us to unconditionally run hooks
        if (!experimentId)
            return;
        // don't assume this effect will only be called once and prevent starting more than one interval
        if (intervalRunning.current)
            return;
        var intervalMs = (options === null || options === void 0 ? void 0 : options.interval) || 50;
        intervalId.current = setInterval(function () {
            intervalRunning.current = true;
            // once Optimize is ready, set the variantId state
            if (window.google_optimize !== undefined) {
                var localVariantId = window.google_optimize.get(experimentId);
                // Optimize is ready, so we can turn off the timer that defaults to variant 0 when Optimize takes too long
                if (timeout.current)
                    clearTimeout(timeout.current);
                // Optimize is ready, so we can stop waiting for it to be ready by ending this interval
                if (intervalId.current)
                    clearInterval(intervalId.current);
                if (localVariantId !== undefined) {
                    setVariantId(localVariantId);
                }
                else {
                    // If window.google_optimize.get returns undefined, the experiment is turned off or misconfigured, so we default to variant 0 (the "original" variant)
                    setVariantId('0');
                }
            }
        }, intervalMs);
        return function () {
            if (intervalId.current)
                clearInterval(intervalId.current);
        };
    }, [experimentId, options === null || options === void 0 ? void 0 : options.interval]);
    // don't wait indefinitely; eventually just stop checking and default to choosing the normal experience
    (0, react_1.useEffect)(function () {
        // perform a "disable" that conforms to the rules of hooks by allowing us to unconditionally run hooks
        if (!experimentId)
            return;
        // don't assume this effect will only be called once and prevent starting more than one timeout
        if (timeoutRunning.current)
            return;
        var timeoutMs = (options === null || options === void 0 ? void 0 : options.timeout) || 500;
        timeout.current = setTimeout(function () {
            timeoutRunning.current = true;
            setVariantId('0');
            var message = 'Timed out waiting for Optimize to choose variant of experiment';
            console.error(message);
            if (window.Sentry) {
                window.Sentry.captureException(new Error(message), {
                    level: 'warning',
                    extra: {
                        experimentId: experimentId,
                        hint: 'Optimize may not be loaded on this page',
                    },
                });
            }
        }, timeoutMs);
        return function () {
            if (timeout.current)
                clearTimeout(timeout.current);
        };
    }, [experimentId, options === null || options === void 0 ? void 0 : options.timeout]);
    return variantId;
}
exports.useExperiment = useExperiment;
